import {
    createContext,
    Dispatch,
    SetStateAction,
    useEffect,
    useMemo,
    useState
} from "react"
import { DocedFeature, Doced } from "ramper"
import { AnnotationFieldsShape } from "resha"
import { AnnotationFields } from "universal-schema/lib/1.0.0/annotation"

import { useBoolean } from "../../../../../Hooks/useBoolean"
import { FieldModalOptions } from "../Common/Hooks/useDocumentsListItem"
import { FieldModalType } from "../Common/Modals/FieldModal"
import { BoundingBoxStatus, BoundingBoxStatusStyles } from "./constants"
import {
    convertToStyledDocedBoxFeature,
    getDefaultDocumentExtraction
} from "./utils"

export type AnnotationMode = "confirm" | "edit"

export type BoundingBoxesType = {
    boundingBoxes: DocedFeature[]
    shapesState: AnnotationFieldsShape
}
export interface AnnotationFieldsStateContext {
    /**
     * Detects was the bounding box changed or not
     */
    changed: boolean
    /**
     * Visible document URL,
     * used for:
     *  rendering in Doced,
     *  assign submitted bounding boxes to specified document
     */
    visibleDocument: string | undefined
    /**
     * Status of selected bounding box
     */
    status: BoundingBoxStatus | undefined
    /**
     * Current visible page, used for recreation of selected bounding box on the next page
     */
    visiblePage: number | undefined
    /**
     * Duplicates showFieldModal variable from useDocumentsListItem.
     * Used only in visual onboarding components to manipulate showing state in renderer and pane
     */
    showFieldModal: boolean
    /**
     * Duplicates fieldModalOptions variable from useDocumentsListItem.
     * Used only in visual onboarding components to manipulate showing state in renderer and pane
     */
    fieldModalOptions: FieldModalOptions
    /**
     * Selected bounding box id in Doced
     */
    selectedId: string | undefined
    /**
     * List of current visible bounding boxes in Doced
     */
    currentBoundingBoxes: BoundingBoxesType | undefined
    /**
     * Transformed detected bounding boxes into doced features and their ids
     */
    detectedBoundingBoxes: BoundingBoxesType
    /**
     * Transformed user processed bounding boxes into doced features and their ids
     */
    processedBoundingBoxes: BoundingBoxesType
    /**
     * Used to store actual extracted content
     */
    currentAnnotations: AnnotationFields[""] | undefined
    /**
     * Initial detected annotations, part of schema_instance
     */
    detectedAnnotations: AnnotationFields
    /**
     * Initial processed annotations, part of formData
     */
    processedAnnotations: AnnotationFields
    /**
     * Doced module render heplers
     */
    rendererHelpers: Doced.DocedHelpers | undefined
    setChanged: (state: boolean) => void
    setStatus: (status?: BoundingBoxStatus) => void
    setVisiblePage: (page?: number) => void
    setVisibleDocument: (document: string) => void
    openNewField: () => void
    openFieldEdit: (options: FieldModalOptions) => void
    closeFieldModal: () => void
    setSelectedId: (item?: string) => void
    setCurrentBoundingBoxes: Dispatch<
        SetStateAction<BoundingBoxesType | undefined>
    >
    setDetectedBoundingBoxes: (items: BoundingBoxesType) => void
    setProcessedBoundingBoxes: (items: BoundingBoxesType) => void
    setCurrentAnnotations: (annotation?: AnnotationFields[""]) => void
    setDetectedAnnotations: (items: AnnotationFields) => void
    setProcessedAnnotations: (items: AnnotationFields) => void
    setRendererHelpers: (rendererHelpers: Doced.DocedHelpers) => void
    assignDefaultBoundingBox: () => void
    assignBoundingBox: (
        annotations: AnnotationFields[""],
        status: BoundingBoxStatus
    ) => void
    dismissBoundingBox: () => void
    makeCurrentBoundingBoxesActive: () => void
}

export const AnnotationFieldsStateContext =
    createContext<AnnotationFieldsStateContext>({
        changed: false,
        status: undefined,
        visibleDocument: undefined,
        visiblePage: undefined,
        showFieldModal: false,
        fieldModalOptions: {},
        selectedId: undefined,
        currentBoundingBoxes: undefined,
        detectedBoundingBoxes: { boundingBoxes: [], shapesState: {} },
        processedBoundingBoxes: { boundingBoxes: [], shapesState: {} },
        currentAnnotations: undefined,
        detectedAnnotations: {},
        processedAnnotations: {},
        rendererHelpers: undefined,
        setChanged: () => null,
        setStatus: () => null,
        setVisiblePage: () => null,
        setVisibleDocument: () => null,
        openNewField: () => null,
        openFieldEdit: () => null,
        closeFieldModal: () => null,
        setSelectedId: () => null,
        setCurrentBoundingBoxes: () => null,
        setDetectedBoundingBoxes: () => null,
        setProcessedBoundingBoxes: () => null,
        setCurrentAnnotations: () => null,
        setDetectedAnnotations: () => null,
        setProcessedAnnotations: () => null,
        setRendererHelpers: () => null,
        assignDefaultBoundingBox: () => null,
        assignBoundingBox: () => null,
        dismissBoundingBox: () => null,
        makeCurrentBoundingBoxesActive: () => null
    })

export const useAnnotationFieldsState = (): AnnotationFieldsStateContext => {
    const [changed, , , , setChanged] = useBoolean(false)

    const [visibleDocument, setVisibleDocument] = useState<string>()

    const [status, setStatus] = useState<BoundingBoxStatus>()

    const [visiblePage, setVisiblePage] = useState<number>()

    const [showFieldModal, openFieldModal, closeFieldModal] = useBoolean(false)

    const [fieldModalOptions, setFieldModalOptions] =
        useState<FieldModalOptions>({})

    const [selectedId, setSelectedId] = useState<string>()

    const [currentBoundingBoxes, setCurrentBoundingBoxes] =
        useState<BoundingBoxesType>()

    const [detectedBoundingBoxes, setDetectedBoundingBoxes] =
        useState<BoundingBoxesType>({ boundingBoxes: [], shapesState: {} })

    const [processedBoundingBoxes, setProcessedBoundingBoxes] =
        useState<BoundingBoxesType>({ boundingBoxes: [], shapesState: {} })

    const [currentAnnotations, setCurrentAnnotations] =
        useState<AnnotationFields[""]>()

    const [detectedAnnotations, setDetectedAnnotations] =
        useState<AnnotationFields>({})

    const [processedAnnotations, setProcessedAnnotations] =
        useState<AnnotationFields>({})

    const [rendererHelpers, setRendererHelpers] = useState<Doced.DocedHelpers>()

    /**
     * List of available to process bounding boxes
     */
    const availableBoundingBoxes: BoundingBoxesType = useMemo(
        () => ({
            boundingBoxes: [
                ...detectedBoundingBoxes.boundingBoxes,
                ...processedBoundingBoxes.boundingBoxes
            ],
            shapesState: {
                ...detectedBoundingBoxes.shapesState,
                ...processedBoundingBoxes.shapesState
            }
        }),
        [detectedBoundingBoxes, processedBoundingBoxes]
    )

    useEffect(() => {
        if (availableBoundingBoxes) {
            setCurrentBoundingBoxes(availableBoundingBoxes)
        }
    }, [availableBoundingBoxes])

    const handleOpenFieldModal = () => {
        setStatus(BoundingBoxStatus.ACTIVE)
        setCurrentBoundingBoxes(undefined)
        setFieldModalOptions({
            index: undefined,
            type: FieldModalType.New
        })
        openFieldModal()
    }

    const handleEditField = (options: FieldModalOptions) => {
        setFieldModalOptions(options)
        openFieldModal()
    }

    const handleCloseEditField = () => {
        closeFieldModal()
        rendererHelpers?.selectInstances([])
        setChanged(false)
        setStatus(undefined)
        setCurrentAnnotations(undefined)
        setCurrentBoundingBoxes(availableBoundingBoxes)
    }

    const assignDefaultBoundingBox = () => {
        if (visiblePage) {
            const newField = getDefaultDocumentExtraction(visiblePage)
            const docedFeature = convertToStyledDocedBoxFeature(
                newField,
                BoundingBoxStatus.ACTIVE
            )
            setCurrentBoundingBoxes(docedFeature)
            setCurrentAnnotations(
                Object.values(newField.schema_instance.annotations)[0]
            )
            rendererHelpers?.selectInstances([docedFeature.boundingBoxes[0].id])
        }
    }

    const assignBoundingBox = (
        annotations: AnnotationFields[""],
        status: BoundingBoxStatus
    ) => {
        // TODO: Table type implementation
        // const documentExtraction = getAnnotationDocumentExtraction({
        //     annotations
        // })
        // const { boundingBoxes } = convertToStyledDocedBoxFeature(
        //     documentExtraction,
        //     status
        // )
        // setCurrentBoundingBoxes(boundingBoxes)
        // setSelectedAnnotation(
        //     Object.values(documentExtraction.schema_instance.annotations)[0]
        // )
        // setStatus(status)
        // rendererHelpers?.selectInstances([boundingBoxes[0].id])
    }

    const dismissBoundingBox = () => {
        setCurrentBoundingBoxes(undefined)
        setCurrentAnnotations(undefined)
        rendererHelpers?.selectInstances([])
    }

    const makeCurrentBoundingBoxesActive = () => {
        const exportedBoundingBoxes = rendererHelpers?.exportInitialScaleValue()
        rendererHelpers?.changeEditingFeature({
            style: BoundingBoxStatusStyles[BoundingBoxStatus.ACTIVE]
        })
        if (exportedBoundingBoxes && currentBoundingBoxes) {
            setStatus(BoundingBoxStatus.ACTIVE)

            setCurrentBoundingBoxes({
                ...currentBoundingBoxes,
                boundingBoxes: exportedBoundingBoxes.map(boundingBox => ({
                    ...boundingBox,
                    style: BoundingBoxStatusStyles[BoundingBoxStatus.ACTIVE]
                }))
            })
        }
    }

    return {
        changed,
        visibleDocument,
        status,
        visiblePage,
        showFieldModal,
        fieldModalOptions,
        selectedId,
        currentBoundingBoxes,
        detectedBoundingBoxes,
        processedBoundingBoxes,
        currentAnnotations,
        detectedAnnotations,
        processedAnnotations,
        rendererHelpers,
        setChanged,
        setStatus,
        setVisiblePage,
        setVisibleDocument,
        openNewField: handleOpenFieldModal,
        openFieldEdit: handleEditField,
        closeFieldModal: handleCloseEditField,
        setSelectedId,
        setCurrentBoundingBoxes,
        setDetectedBoundingBoxes,
        setProcessedBoundingBoxes,
        setCurrentAnnotations,
        setDetectedAnnotations,
        setProcessedAnnotations,
        setRendererHelpers,
        assignDefaultBoundingBox,
        assignBoundingBox,
        dismissBoundingBox,
        makeCurrentBoundingBoxesActive
    }
}
